import { useState, useEffect, useRef } from 'react';
import { LogOut, ChevronDown, Settings, Compass } from 'lucide-react';
import { ByteLogo } from '../ByteLogo';
import type { User } from './types';

interface HeaderProps {
  user: User;
  authMethod: 'mapp' | 'email' | null;
  environment: string | null;
  brandId: string | null;
  onLogout: () => void;
  onSettingsClick: () => void;
  onAppTourClick?: () => void;
  /** When false (e.g. HutBot webview with window.mapp), Logout is hidden — host handles session */
  showLogout?: boolean;
  currentPageLabel: string;
}

export function Header({
  user,
  authMethod: _authMethod,
  environment: _environment,
  onLogout,
  onSettingsClick,
  onAppTourClick,
  showLogout = true,
  currentPageLabel,
}: HeaderProps) {
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const userInitials = `${user.firstName.charAt(0)}${user.lastName?.charAt(0) || ''}`;

  // Close dropdown when clicking outside
  useEffect(() => {
    if (!isUserMenuOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setIsUserMenuOpen(false);
      }
    };

    // Close on escape key
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsUserMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isUserMenuOpen]);

  return (
    <header className="sticky top-0 z-20 bg-white border-b border-gray-200">
      <div className="flex items-center justify-between h-16 px-4 sm:px-6">
        {/* Left side */}
        <div className="flex items-center gap-4">
          {/* Logo (shown on mobile only) */}
          <div className="flex lg:hidden items-center gap-2">
            <ByteLogo className="w-8 h-8" />
          </div>

          {/* Current page title (desktop) */}
          <div className="block">
            <h1 className="text-xl font-semibold text-gray-900">{currentPageLabel}</h1>
          </div>
        </div>

        {/* Right side */}
        <div className="flex items-center gap-2 sm:gap-3">
          {/* Divider */}
          <div className="hidden sm:block w-px h-8 bg-gray-200" />

          {/* User menu */}
          <div className="relative" ref={userMenuRef}>
            {/* Clickable user info */}
            <button
              onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
              className="flex items-center gap-2 sm:gap-3 px-2 py-1.5 rounded-lg hover:bg-gray-100 transition-colors"
              aria-label="User menu"
              aria-expanded={isUserMenuOpen}
            >
              {/* User name and email */}
              <div className="hidden sm:block text-right">
                <p className="text-sm font-semibold text-gray-900 truncate max-w-[140px] md:max-w-[180px]">
                  {user.firstName} {user.lastName}
                </p>
                <p className="text-xs text-gray-500 truncate max-w-[140px] md:max-w-[180px]">
                  {user.email}
                </p>
              </div>

              {/* Avatar */}
              <div className="w-9 h-9 rounded-full bg-gradient-to-br from-byte-orange via-byte-crimson to-byte-purple flex items-center justify-center text-white text-sm font-semibold flex-shrink-0">
                {userInitials}
              </div>

              {/* Chevron icon */}
              <ChevronDown
                className={`w-4 h-4 text-gray-400 transition-transform flex-shrink-0 ${
                  isUserMenuOpen ? 'rotate-180' : ''
                }`}
              />
            </button>

            {/* Dropdown menu */}
            {isUserMenuOpen && (
              <>
                {/* Backdrop for mobile */}
                <div
                  className="fixed inset-0 z-10 lg:hidden"
                  onClick={() => setIsUserMenuOpen(false)}
                />

                {/* Menu */}
                <div className="absolute right-0 mt-2 w-56 bg-white border border-gray-200 rounded-lg shadow-lg z-20 py-1">
                  {/* User info in dropdown (mobile only) */}
                  <div className="sm:hidden px-4 py-3 border-b border-gray-200">
                    <p className="text-sm font-semibold text-gray-900">
                      {user.firstName} {user.lastName}
                    </p>
                    <p className="text-xs text-gray-500 mt-0.5">{user.email}</p>
                  </div>

                  {/* Settings option */}
                  <button
                    onClick={() => {
                      setIsUserMenuOpen(false);
                      onSettingsClick();
                    }}
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                  >
                    <Settings className="w-4 h-4" />
                    <span>Settings</span>
                  </button>

                  {/* App Tour option (internal users only) */}
                  {onAppTourClick && (
                    <button
                      onClick={() => {
                        setIsUserMenuOpen(false);
                        onAppTourClick();
                      }}
                      className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                      data-testid="header-btn-app-tour"
                    >
                      <Compass className="w-4 h-4" />
                      <span>App Tour</span>
                    </button>
                  )}

                  {showLogout && (
                    <>
                      <div className="border-t border-gray-200 my-1" />
                      <button
                        onClick={() => {
                          setIsUserMenuOpen(false);
                          onLogout();
                        }}
                        className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-red-50 hover:text-red-600 transition-colors"
                      >
                        <LogOut className="w-4 h-4" />
                        <span>Logout</span>
                      </button>
                    </>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}
